import type {
  AccountAttachmentStoreForm,
  AccountErpForm,
  AccountErpView,
  AttachmentStore,
} from './api.ts'

/**
 * The attachments database as the Conexão tab shows it (#689): in the
 * Receitas section, beside the Drive folder it is the other choice to, and no
 * longer among the ERP connection's fields.
 *
 * On the server both halves are one row, `account_erp_connections`, because
 * the attachments database is read with the connection's user and password.
 * These keep the two sections from saving over each other, and mirror the
 * server's rule on when the password must be typed again.
 */

/** The attachments half of a connection, as its two fields: empty when there is none. */
export interface AttachmentsDatabaseForm {
  readonly database: string
  readonly host: string
}

/** The ERP connection's own fields: its form without the attachments database. */
export type ErpConnectionFields = Omit<AccountErpForm, 'attachmentsDatabase' | 'attachmentsHost'>

export function attachmentsDatabaseOf(view: AccountErpView): AttachmentsDatabaseForm {
  return {
    database: view.connection?.attachmentsDatabase ?? '',
    host: view.connection?.attachmentsHost ?? '',
  }
}

/** Whether two forms name the same attachments database; blank and absent are one. */
export function sameAttachmentsDatabase(
  a: AttachmentsDatabaseForm,
  b: AttachmentsDatabaseForm,
): boolean {
  return a.database.trim() === b.database.trim() && a.host.trim() === b.host.trim()
}

/**
 * Whether saving `draft` needs the ERP's password typed again.
 *
 * The server's `keepsStoredPassword`: a stored password goes only where it
 * was saved to go, so naming another attachments database or host asks for
 * it. Emptying the database is the one change that keeps it -- the password
 * then goes to one place fewer, never to a new one.
 */
export function attachmentsNeedPassword(
  view: AccountErpView,
  draft: AttachmentsDatabaseForm,
): boolean {
  return (
    !sameAttachmentsDatabase(attachmentsDatabaseOf(view), draft) && draft.database.trim() !== ''
  )
}

/**
 * The ERP section's fields as the route takes them, with the attachments
 * database the account has saved now -- not the one it had when the section
 * was opened, which the Receitas section may have saved over since.
 */
export function withSavedAttachments(
  fields: ErpConnectionFields,
  view: AccountErpView,
): AccountErpForm {
  const attachments = attachmentsDatabaseOf(view)
  return { ...fields, attachmentsDatabase: attachments.database, attachmentsHost: attachments.host }
}

/**
 * The saved connection as the route takes it, with `attachments` in place of
 * its own and `password` as typed: empty keeps the stored one, which the
 * server allows only where `attachmentsNeedPassword` says it may.
 *
 * Undefined for an account with no connection of its own: an attachments
 * database is part of one, and is not saved alone.
 */
export function connectionWithAttachments(
  view: AccountErpView,
  attachments: AttachmentsDatabaseForm,
  password: string,
): AccountErpForm | undefined {
  const saved = view.connection
  if (saved === null) return undefined
  return {
    host: saved.host,
    port: saved.port,
    database: saved.database,
    attachmentsDatabase: attachments.database.trim(),
    attachmentsHost: attachments.host.trim(),
    user: saved.user,
    password,
    charset: saved.charset,
    authPlugin: saved.authPlugin,
    wireCrypt: saved.wireCrypt,
    statementTimeoutMs: saved.statementTimeoutMs,
  }
}

const NO_ATTACHMENTS: AttachmentsDatabaseForm = { database: '', host: '' }

/**
 * What saving the Receitas section makes of the attachments database:
 * undefined to leave it as it is.
 *
 * With a Drive folder chosen it is emptied. Prescriptions in a folder are
 * never read from it, but the connection's own test still dials every
 * database the connection names, and a leftover one would fail it with
 * nowhere on the page to clear it. Emptying asks for no password.
 */
export function attachmentsTarget(
  view: AccountErpView,
  store: AttachmentStore,
  typed: AttachmentsDatabaseForm,
): AttachmentsDatabaseForm | undefined {
  if (view.connection === null) return undefined
  const target = store === 'erp_database' ? typed : NO_ATTACHMENTS
  return sameAttachmentsDatabase(attachmentsDatabaseOf(view), target) ? undefined : target
}

/** How "Testar" in Receitas tries what is chosen, as typed. */
export type ReceitasTest =
  /** A typed attachments database, through the connection, which dials both of its databases. */
  | { readonly kind: 'connection'; readonly draft: AccountErpForm }
  /** The store as saved, or the choice as typed when it changed. */
  | { readonly kind: 'store'; readonly draft: AccountAttachmentStoreForm | undefined }
  /** Nothing that could answer: said instead of a success nothing earned. */
  | { readonly kind: 'nothing'; readonly reason: string }

export function receitasTest(
  view: AccountErpView,
  choice: AccountAttachmentStoreForm,
  typed: AttachmentsDatabaseForm,
  password: string,
  storeDirty: boolean,
): ReceitasTest {
  const storeDraft = storeDirty ? choice : undefined
  if (choice.store === 'google_drive') return { kind: 'store', draft: storeDraft }
  const target = attachmentsTarget(view, choice.store, typed)
  if (target === undefined) return { kind: 'store', draft: storeDraft }
  if (target.database === '') {
    return { kind: 'nothing', reason: 'Informe o banco de anexos para testar.' }
  }
  const draft = connectionWithAttachments(view, target, password)
  return draft === undefined ? { kind: 'store', draft: storeDraft } : { kind: 'connection', draft }
}

/** One of the two requests a Receitas save may make. */
export type ReceitasStep = 'connection' | 'store'

/**
 * In which order a Receitas save makes its requests; a refused one stops the rest.
 *
 * The connection goes first while the ERP database stays chosen: it is the
 * half that can refuse, for want of the password, and a store that changes
 * after it never points at a database that is not saved. Choosing a Drive
 * folder empties the database, so the store goes first: a folder refused --
 * no link, a bad one -- must leave the account still reading the database it
 * reads, never on the ERP database with none.
 */
export function receitasSaveOrder(
  store: AttachmentStore,
  connection: boolean,
  storeDirty: boolean,
): readonly ReceitasStep[] {
  const steps: ReceitasStep[] =
    store === 'google_drive' ? ['store', 'connection'] : ['connection', 'store']
  return steps.filter((step) => (step === 'connection' ? connection : storeDirty))
}
